import { useWatch, type Control } from 'react-hook-form'

import { CheckboxField, SelectField, TextField } from '@/components/form/fields'

import { REGION_OPTIONS } from '../constants'
import { formatGhanaPostGpsInput } from '../validation'
import type { CustomerWizardInput } from '../validation'
import { FormattedTextField } from './formatted-field'
import { StepSection } from './step-section'

type AddressPrefix = 'residence' | 'business'

function AddressFields({
  control,
  prefix,
}: {
  control: Control<CustomerWizardInput>
  prefix: AddressPrefix
}) {
  return (
    <>
      <FormattedTextField
        control={control}
        name={`${prefix}.ghanaPostGps`}
        label="GhanaPost digital address"
        required
        placeholder="GA-183-8164"
        description="Read from the customer's GhanaPost app, or looked up from the location."
        format={formatGhanaPostGpsInput}
      />
      <TextField
        control={control}
        name={`${prefix}.streetOrArea`}
        label="Street, house number, or area"
        required
      />
      <TextField
        control={control}
        name={`${prefix}.landmark`}
        label="Landmark"
        description="What a field officer would actually navigate by."
      />
      <TextField control={control} name={`${prefix}.town`} label="Town or suburb" required />
      <TextField control={control} name={`${prefix}.district`} label="District" required />
      <SelectField
        control={control}
        name={`${prefix}.region`}
        label="Region"
        options={REGION_OPTIONS}
        placeholder="Select a region…"
        required
      />
    </>
  )
}

export function AddressStep({ control }: { control: Control<CustomerWizardInput> }) {
  const hasBusinessAddress = useWatch({ control, name: 'hasBusinessAddress' })

  return (
    <div className="space-y-6">
      <StepSection
        title="Residence"
        description="Required. It is also the address-verification evidence the customer due-diligence file rests on."
      >
        <AddressFields control={control} prefix="residence" />
      </StepSection>

      <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
        <CheckboxField
          control={control}
          name="hasBusinessAddress"
          label="The customer trades from a separate business address"
          description="Capture it where there is one — it is where a field officer will find them on a working day."
        />
      </div>

      {hasBusinessAddress ? (
        <StepSection title="Business address">
          <AddressFields control={control} prefix="business" />
        </StepSection>
      ) : null}

      <p className="text-xs text-muted">
        GPS coordinates are not typed here. They are captured by the field application against a
        real fix, because a pair of numbers keyed at a desk is evidence of nothing.
      </p>
    </div>
  )
}
